import { useEffect, useRef, useState } from 'react';

const DELETE_CONFIRM_MIN_DELAY_MS = 300;

export interface DraftDeleteArmApi {
  deleteArmed: boolean;
  handleDeleteClick: () => void;
}

export function useDraftDeleteArm(params: {
  deleteDisabled: boolean;
  deleteError: string | null;
  onDelete: () => Promise<void> | void;
}): DraftDeleteArmApi {
  const { deleteDisabled, deleteError, onDelete } = params;
  // INVARIANT: destructive delete is two-step, mirroring
  // DraftCloseConfirmModal — first activation only flips the armed
  // flag, the second accepted activation (after >=300 ms) calls
  // `onDelete`. Armed state does NOT auto-reset on a timer; it
  // resets only on the explicit transitions below.
  const [deleteArmed, setDeleteArmed] = useState(false);
  const deleteArmedAtRef = useRef<number | null>(null);

  // Reset armed state when delete becomes unavailable mid-flight (e.g.
  // upload starts while armed). Edge-only: a re-arm after the busy
  // window clears must not be auto-cancelled.
  useEffect(() => {
    if (deleteArmed && deleteDisabled) {
      setDeleteArmed(false);
      deleteArmedAtRef.current = null;
    }
  }, [deleteDisabled, deleteArmed]);

  // Reset armed on the EDGE where the parent reports a fresh delete
  // failure (null → non-null). Tracking the prior value via a ref so a
  // sticky `deleteError` doesn't keep clobbering re-arm attempts the
  // user makes after the failure.
  const prevDeleteErrorRef = useRef<string | null>(deleteError);
  useEffect(() => {
    const prev = prevDeleteErrorRef.current;
    prevDeleteErrorRef.current = deleteError;
    if (prev === null && deleteError !== null) {
      setDeleteArmed(false);
      deleteArmedAtRef.current = null;
    }
  }, [deleteError]);

  function handleDeleteClick(): void {
    if (deleteDisabled) return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      deleteArmedAtRef.current = Date.now();
      return;
    }
    const armedAt = deleteArmedAtRef.current;
    if (armedAt !== null && Date.now() - armedAt < DELETE_CONFIRM_MIN_DELAY_MS) {
      // Swallow the rapid double-activation; the user must wait the
      // minimum delay before the second accepted activation.
      return;
    }
    // The drawer's `performDelete` handler routes by persistence
    // state: persisted row → DELETE; never-saved scratchpad → cleanup
    // + closeDraft. Same armed-confirm ceremony either way.
    void onDelete();
  }

  return { deleteArmed, handleDeleteClick };
}
